import React, { useState, useEffect } from 'react';
import { leadsAPI } from '../lib/api';
import { formatDate, PIPELINE_STAGES, PRACTICE_AREAS } from '../lib/utils';
import { 
  UserPlus, 
  Plus, 
  Mail, 
  Phone,
  MoreVertical,
  Edit,
  Trash2,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

export const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: '',
    practice_area: '',
    notes: '',
    pipeline_stage: 'inquiry',
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await leadsAPI.update(editingLead.lead_id, formData);
      } else {
        await leadsAPI.create(formData);
      }
      fetchLeads();
      resetForm();
    } catch (error) {
      console.error('Failed to save lead:', error);
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email || '',
      phone: lead.phone || '',
      source: lead.source || '',
      practice_area: lead.practice_area || '',
      notes: lead.notes || '',
      pipeline_stage: lead.pipeline_stage,
    });
    setIsDialogOpen(true);
  };

  const handleConvert = async (leadId) => {
    if (window.confirm('Convert this lead to a client?')) {
      try {
        await leadsAPI.convert(leadId);
        fetchLeads();
      } catch (error) {
        console.error('Failed to convert lead:', error);
      }
    }
  };

  const handleStageChange = async (lead, newStage) => {
    try {
      await leadsAPI.update(lead.lead_id, { ...lead, pipeline_stage: newStage });
      fetchLeads();
    } catch (error) {
      console.error('Failed to update lead stage:', error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', source: '', practice_area: '', notes: '', pipeline_stage: 'inquiry' });
    setEditingLead(null);
    setIsDialogOpen(false);
  };

  const getLeadsByStage = (stage) => leads.filter(lead => lead.pipeline_stage === stage && lead.status !== 'converted');

  if (loading) {
    return <PageLoader text="Loading leads..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Lead Pipeline</h1>
          <p className="text-slate-500 mt-1">Track and convert potential clients</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-lead-btn">
              <Plus className="h-4 w-4" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="lead-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="lead-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Website, Referral, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="practice_area">Practice Area</Label>
                  <Select
                    value={formData.practice_area}
                    onValueChange={(value) => setFormData({ ...formData, practice_area: value })}
                  >
                    <SelectTrigger data-testid="lead-practice-area-select">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRACTICE_AREAS.map((area) => (
                        <SelectItem key={area} value={area}>{area}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="pipeline_stage">Pipeline Stage</Label>
                  <Select
                    value={formData.pipeline_stage}
                    onValueChange={(value) => setFormData({ ...formData, pipeline_stage: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPELINE_STAGES.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" data-testid="save-lead-btn">
                  {editingLead ? 'Update' : 'Create'} Lead
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {PIPELINE_STAGES.map((stage) => {
          const stageLeads = getLeadsByStage(stage.value);
          return (
            <div key={stage.value} className="min-w-[280px]">
              <div className={`rounded-t-lg px-4 py-3 ${stage.color}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-900">{stage.label}</h3>
                  <Badge variant="secondary" className="bg-white/50">
                    {stageLeads.length}
                  </Badge>
                </div>
              </div>
              <div className="bg-slate-50 rounded-b-lg p-3 min-h-[400px] space-y-3">
                {stageLeads.map((lead) => (
                  <Card 
                    key={lead.lead_id} 
                    className="pipeline-card"
                    data-testid={`lead-card-${lead.lead_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">{lead.name}</h4>
                          {lead.practice_area && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {lead.practice_area}
                            </Badge>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(lead)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleConvert(lead.lead_id)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Convert to Client
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="mt-3 space-y-1">
                        {lead.email && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Phone className="h-3 w-3" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                      </div>

                      {stage.value !== 'closed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full mt-3 text-xs gap-1"
                          onClick={() => {
                            const currentIndex = PIPELINE_STAGES.findIndex(s => s.value === stage.value);
                            const nextStage = PIPELINE_STAGES[currentIndex + 1];
                            if (nextStage) handleStageChange(lead, nextStage.value);
                          }}
                        >
                          Move to next <ArrowRight className="h-3 w-3" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {stageLeads.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No leads in this stage
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Leads;
